import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal, X, BellPlus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useMarket } from "@/lib/market";
import { useAuth } from "@/lib/auth";
import { themeStyle } from "@/lib/fmt";
import ListingCard from "@/components/ListingCard";
import { Loading, Empty, ErrorBox } from "@/components/Bits";

const LAKH = 100000;
const SELECTS = [
  ["verification", "Verification", [["any_reviewed", "Any area reviewed"], ["financials_reviewed", "Financials reviewed"], ["independently_verified", "Independently verified"]]],
  ["sale_type", "Sale type", [["full_acquisition", "Full acquisition"], ["asset_sale", "Asset sale"]]],
  ["operation_mode", "Format", [["physical", "Physical"], ["online", "Online"], ["hybrid", "Hybrid"]]],
  ["management", "Management", [["owner_managed", "Owner-managed"], ["manager_run", "Manager-run"]]],
  ["availability", "Availability", [["available", "Available"], ["under_offer", "Under offer"], ["sold", "Sold"]]],
];
const RANGES = [["price", "Asking price (₹ lakh)"], ["rev", "Annual revenue (₹ lakh)"], ["profit", "Annual profit (₹ lakh)"], ["age", "Business age (years)"]];

export default function Browse() {
  const { category, city } = useParams();
  const [sp, setSp] = useSearchParams();
  const { meta, savedIds, toggleSave, compare, toggleCompare, catName } = useMarket();
  const { user } = useAuth();
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const [showF, setShowF] = useState(false);
  const f = Object.fromEntries(sp.entries());
  const page = Number(f.page || 1);
  const cityName = city ? meta.cities.find((c) => c.slug === city)?.name || city : f.city;
  const cat = category || f.category || "";

  const params = useMemo(() => {
    const p = { q: f.q, category: cat, state: f.state, city: cityName, sort: f.sort || "newest", page, page_size: 12 };
    SELECTS.forEach(([k]) => { if (f[k]) p[k] = f[k]; });
    RANGES.forEach(([k]) => {
      const mul = k === "age" ? 1 : LAKH;
      if (f[`${k}_min`]) p[`${k}_min`] = Number(f[`${k}_min`]) * mul;
      if (f[`${k}_max`]) p[`${k}_max`] = Number(f[`${k}_max`]) * mul;
    });
    Object.keys(p).forEach((k) => (p[k] === undefined || p[k] === "") && delete p[k]);
    return p;
  }, [sp, cat, cityName]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setRes(null); setErr("");
    api.get("/public/listings", { params }).then((r) => setRes(r.data)).catch((e) => setErr(errMsg(e)));
  }, [params]);

  const set = (k, v) => { const n = new URLSearchParams(sp); if (v === "" || v == null) n.delete(k); else n.set(k, v); n.delete("page"); setSp(n); };
  const clear = () => setSp(new URLSearchParams());
  const activeCount = [...sp.keys()].filter((k) => !["page", "sort"].includes(k)).length;
  const saveSearch = async () => {
    if (!user) return toast("Log in to save searches and get alerts");
    try {
      await api.post("/saved-searches", { name: f.q || catName(cat) || cityName || "My search", query: { q: f.q, category: cat, city: cityName, state: f.state, price_min: params.price_min, price_max: params.price_max, operation_mode: f.operation_mode, sale_type: f.sale_type }, alerts_inapp: true });
      toast.success("Search saved. You'll get in-app alerts for new matches.");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const title = category ? catName(category) : city ? `Businesses in ${cityName}` : "Browse businesses";
  const states = [...new Set(meta.cities.map((c) => c.state))];

  const Filters = (
    <div className="space-y-4" data-testid="filters-panel">
      {!category && <div><label className="lbl" htmlFor="f-cat">Category</label><select id="f-cat" className="inp" value={f.category || ""} onChange={(e) => set("category", e.target.value)} data-testid="filter-category"><option value="">All categories</option>{meta.categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}</select></div>}
      <div><label className="lbl" htmlFor="f-state">State</label><select id="f-state" className="inp" value={f.state || ""} onChange={(e) => set("state", e.target.value)} data-testid="filter-state"><option value="">All states</option>{states.map((s) => <option key={s}>{s}</option>)}</select></div>
      {!city && <div><label className="lbl" htmlFor="f-city">City</label><select id="f-city" className="inp" value={f.city || ""} onChange={(e) => set("city", e.target.value)} data-testid="filter-city"><option value="">All cities</option>{meta.cities.filter((c) => !f.state || c.state === f.state).map((c) => <option key={c.slug} value={c.name}>{c.name}</option>)}</select></div>}
      {RANGES.map(([k, t]) => (
        <div key={k}><p className="lbl">{t}</p><div className="flex gap-2">
          <input className="inp" type="number" min="0" inputMode="decimal" placeholder="Min" aria-label={`${t} minimum`} value={f[`${k}_min`] || ""} onChange={(e) => set(`${k}_min`, e.target.value)} data-testid={`filter-${k}-min`} />
          <input className="inp" type="number" min="0" inputMode="decimal" placeholder="Max" aria-label={`${t} maximum`} value={f[`${k}_max`] || ""} onChange={(e) => set(`${k}_max`, e.target.value)} data-testid={`filter-${k}-max`} />
        </div></div>
      ))}
      {SELECTS.map(([k, t, opts]) => (
        <div key={k}><label className="lbl" htmlFor={`f-${k}`}>{t}</label><select id={`f-${k}`} className="inp" value={f[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`filter-${k}`}><option value="">Any</option>{opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
      ))}
      <button className="btn-ghost w-full" onClick={clear} data-testid="clear-filters-btn">Clear filters</button>
    </div>
  );

  return (
    <div className="atmosphere min-h-screen" style={themeStyle(category)}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <p className="eyebrow">{category ? "Category" : city ? "City" : "Marketplace"}</p>
        <h1 className="text-3xl sm:text-4xl text-[#29483D] mt-1" data-testid="browse-title">{title}</h1>
        <div className="mt-6 flex flex-col sm:flex-row gap-2">
          <input className="inp sm:max-w-md" placeholder="Search by business, category or city" defaultValue={f.q || ""} onKeyDown={(e) => e.key === "Enter" && set("q", e.target.value)} aria-label="Search" data-testid="browse-search-input" />
          <select className="inp sm:max-w-[200px]" value={f.sort || "newest"} onChange={(e) => set("sort", e.target.value)} aria-label="Sort" data-testid="sort-select">
            <option value="newest">Newest</option><option value="price_asc">Price: low to high</option><option value="price_desc">Price: high to low</option><option value="revenue_desc">Highest revenue</option><option value="profit_desc">Highest profit</option><option value="popular">Most viewed</option>
          </select>
          <button className="btn-ghost lg:hidden" onClick={() => setShowF(true)} data-testid="open-filters-btn"><SlidersHorizontal size={15} /> Filters {activeCount ? `(${activeCount})` : ""}</button>
          <button className="btn-ghost" onClick={saveSearch} data-testid="save-search-btn"><BellPlus size={15} /> Save search</button>
        </div>
        <div className="grid lg:grid-cols-[260px_1fr] gap-8 mt-8">
          <aside className="hidden lg:block card p-5 h-fit sticky top-24">{Filters}</aside>
          {showF && (
            <div className="fixed inset-0 z-50 bg-black/30 lg:hidden" onClick={() => setShowF(false)}>
              <div className="absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto bg-[#F7F3EB] rounded-t-3xl p-5" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4"><p className="font-display text-xl">Filters</p><button onClick={() => setShowF(false)} aria-label="Close filters" className="btn-ghost btn-sm"><X size={16} /></button></div>
                {Filters}
                <button className="btn-primary w-full mt-3" onClick={() => setShowF(false)}>Show {res?.total ?? ""} results</button>
              </div>
            </div>
          )}
          <div>
            <ErrorBox msg={err} />
            {!res && !err ? <Loading /> : res && (
              <>
                <p className="text-sm text-stone-600 mb-4" data-testid="results-count">{res.total} {res.total === 1 ? "business" : "businesses"}</p>
                {res.items.length === 0 ? (
                  <Empty title="No businesses match these filters" body="Try widening your price range or clearing filters. You can also save this search to get an alert when something new is published." action={<button className="btn-primary" onClick={clear}>Clear filters</button>} testid="browse-empty" />
                ) : (
                  <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
                    {res.items.map((l) => <ListingCard key={l.id} l={l} saved={savedIds.includes(l.id)} onSave={toggleSave} inCompare={compare.includes(l.id)} onCompare={toggleCompare} catName={catName(l.data.category)} />)}
                  </div>
                )}
                {res.total > res.page_size && (
                  <div className="flex justify-center gap-2 mt-10" data-testid="pagination">
                    <button className="btn-ghost btn-sm" disabled={page <= 1} onClick={() => { const n = new URLSearchParams(sp); n.set("page", page - 1); setSp(n); }}>Previous</button>
                    <span className="text-sm self-center">Page {page} of {Math.ceil(res.total / res.page_size)}</span>
                    <button className="btn-ghost btn-sm" disabled={page * res.page_size >= res.total} onClick={() => { const n = new URLSearchParams(sp); n.set("page", page + 1); setSp(n); }}>Next</button>
                  </div>
                )}
              </>
            )}
            {compare.length > 0 && <Link to="/compare" className="btn-accent fixed bottom-24 lg:bottom-8 right-4 z-30 shadow-lg" data-testid="floating-compare-btn">Compare {compare.length}</Link>}
          </div>
        </div>
      </div>
    </div>
  );
}
